/**
 * Rights & Splits role taxonomy — shared by the ParticipantPanel UI and the
 * server (BB Copilot's setSplit). Dependency-free.
 *
 * Rights pools (each sums to 100 % independently):
 *   Writing     — song composition (writers only)
 *   Publishing  — exploitation / admin (writers + publishers share)
 *   Performance — recorded performance (artists, musicians, producers)
 */

export const WRITER_ROLES   = ["Writer", "Songwriter", "Composer", "Lyricist", "Co-Writer"];
export const PUBLISHER_ROLES = ["Publisher", "Co-Publisher", "Sub-Publisher"];
export const PERFORMANCE_ROLES = [
  "Primary Artist", "Featured Artist", "Vocalist", "Rapper",
  "Producer", "Co-Producer", "Executive Producer", "Remixer",
  "Musician", "Session Musician", "DJ", "Arranger",
];
/** Non-copyright "thanks" credits — recognition only, never claim a % split. */
export const THANKS_ROLES = [
  "Family member", "Friend", "Sponsor", "Inspiration", "Brand",
  "Mentor", "Manager", "Engineer", "Mix Engineer", "Mastering Engineer",
  "Photographer", "Videographer", "Designer", "Stylist",
  "Label", "Distributor", "Studio", "Venue", "Crew",
];
export const ALL_ROLES = [...WRITER_ROLES, ...PUBLISHER_ROLES, ...PERFORMANCE_ROLES, ...THANKS_ROLES];

export function roleCategory(role: string): "writing" | "publishing" | "performance" {
  if (WRITER_ROLES.includes(role))     return "writing";
  if (PUBLISHER_ROLES.includes(role))  return "publishing";
  return "performance";
}

/**
 * Map a free-text Contributor role/instrument credit (DB-driven, e.g.
 * "Producer", "Trumpet") onto the fixed Rights & Splits role taxonomy —
 * used to turn Contributors into promotable rights participants (Gordon
 * 2026-09-30: "Turn Contributors into Rights & Splits owners"). Exact match
 * first, then keyword heuristics for common near-misses, then default into
 * the Performance pool with a generic "Musician" credit (Gordon: best-guess
 * map, default to Performance) rather than blocking on an unmapped role.
 */
export function mapContributorRoleToParticipantRole(roles: string[]): { role: string; creditKind: "split" | "thanks" } {
  const list = roles.map(r => r.trim()).filter(Boolean);
  for (const r of list) {
    const exact = ALL_ROLES.find(a => a.toLowerCase() === r.toLowerCase());
    if (exact) return { role: exact, creditKind: THANKS_ROLES.includes(exact) ? "thanks" : "split" };
  }
  const joined = list.join(" ").toLowerCase();
  const heuristics: [RegExp, string][] = [
    [/produc/, "Producer"], [/mix/, "Mix Engineer"], [/master/, "Mastering Engineer"],
    [/engineer/, "Engineer"], [/writ|compos|lyric/, "Writer"], [/publish/, "Publisher"],
    [/vocal|sing/, "Vocalist"], [/rap/, "Rapper"], [/feat/, "Featured Artist"],
    [/remix/, "Remixer"], [/arrang/, "Arranger"], [/^dj$|\bdj\b/, "DJ"],
    [/photo/, "Photographer"], [/video|film/, "Videographer"], [/design/, "Designer"],
    [/manag/, "Manager"],
  ];
  for (const [re, mapped] of heuristics) {
    if (re.test(joined)) return { role: mapped, creditKind: THANKS_ROLES.includes(mapped) ? "thanks" : "split" };
  }
  return { role: "Musician", creditKind: "split" };
}
